import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Link2, 
  Clock, 
  Save, 
  UserCheck, 
  Calendar, 
  Mail,
  RefreshCw
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import './FormsUnified.css';

const PRESET_LINKS = [
  'register',
  'contact-us#contact-form',
  'contact-us',
  'admissions',
  'education-program',
  'student-life',
  'facilities',
  'news-and-updates',
  'parents',
  'careers',
  'about-us',
  'login',
  '#school-calendar'
];

export default function QuicklinksForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [sectionId, setSectionId] = useState(42);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [customLinkMode, setCustomLinkMode] = useState({});

  // Form values
  const [formData, setFormData] = useState({
    kicker: 'Quick Access',
    title: 'Everything you need in one place',
    cards: [
      { id: 'apply', title: 'Apply Now', text: 'Start your application with clear, step-by-step guidance.', link: 'register' },
      { id: 'calendar', title: 'School Calendar', text: 'View academic dates, events, competitions, ceremonies, cultural days, and field trips.', link: '#school-calendar' },
      { id: 'contact', title: 'Contact Us', text: 'Connect with our team for questions, visits, or admissions support.', link: 'contact-us#contact-form' }
    ]
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({ ...formData });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    'cards.0': false,
    'cards.1': false,
    'cards.2': false
  });

  const loadQuickLinksSection = useCallback(async () => {
    setLoading(true);
    try {
      let response = await fetch(
        `${apiBase}/cms/section/quick_links/with-pending?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/42/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      if (!response.ok) {
        response = await fetch(
          `${apiBase}/cms/section/quicklinks/with-pending?_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      if (!response.ok) {
        response = await fetch(`${apiBase}/homepage/cms/sections?published=true&_t=${Date.now()}`, {
          credentials: 'omit'
        });
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      let section = null;
      let pending_changes = [];

      if (result.success && result.data) {
        section = result.data.section;
        pending_changes = result.data.pending_changes || [];
      } else if (result.ok && Array.isArray(result.data)) {
        section = result.data.find(s => s.section_type === 'quicklinks' || s.id === 42);
      }

      if (section) {
        let published = section.content_published;
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) { published = {}; }
        }

        let draft = section.content;
        if (typeof draft === 'string') {
          try { draft = JSON.parse(draft); } catch (e) { draft = {}; }
        }

        const source = published?.en || published?.id || draft?.en || draft?.id || published || draft || {};

        // Auto-fill form with published data initially
        const initialData = {
          kicker: source.kicker || 'Quick Access',
          title: source.title || 'Everything you need in one place',
          cards: [
            { 
              id: 'apply', 
              title: source.cards?.[0]?.title || 'Apply Now', 
              text: source.cards?.[0]?.text || 'Start your application with clear, step-by-step guidance.',
              link: source.cards?.[0]?.link || 'register'
            },
            { 
              id: 'calendar', 
              title: source.cards?.[1]?.title || 'School Calendar', 
              text: source.cards?.[1]?.text || 'View academic dates, events, competitions, ceremonies, cultural days, and field trips.',
              link: source.cards?.[1]?.link || '#school-calendar'
            },
            { 
              id: 'contact', 
              title: source.cards?.[2]?.title || 'Contact Us', 
              text: source.cards?.[2]?.text || 'Connect with our team for questions, visits, or admissions support.',
              link: source.cards?.[2]?.link || 'contact-us#contact-form'
            }
          ]
        };

        const pendingMap = {
          kicker: false,
          title: false,
          'cards.0': false,
          'cards.1': false,
          'cards.2': false
        };

        // Check global pending approvals if needed
        try {
          const appRes = await fetch(`${apiBase}/homepage/cms/pending-approvals?limit=50`, {
            credentials: 'include'
          });
          if (appRes.ok) {
            const appJson = await appRes.json();
            const items = appJson.data?.items || [];
            items.forEach(item => {
              if (item.section_id === section.id || item.section_type === 'quicklinks') {
                pending_changes.push(item);
              }
            });
          }
        } catch (_) {}

        if (Array.isArray(pending_changes)) {
          pending_changes.forEach(change => {
            if (change.status === 'pending') {
              const fid = change.field_id;
              
              let val = change.new_value;
              if (typeof val === 'string') {
                try { val = JSON.parse(val); } catch (e) {}
              }

              if (fid in pendingMap) {
                pendingMap[fid] = true;
              }

              if (val !== undefined && val !== null) {
                if (fid === 'kicker') {
                  initialData.kicker = val;
                } else if (fid === 'title') {
                  initialData.title = val;
                } else if (fid.startsWith('cards.')) {
                  const parts = fid.split('.');
                  const idx = parseInt(parts[1], 10);
                  if (!isNaN(idx) && initialData.cards[idx]) {
                    if (typeof val === 'object') {
                      initialData.cards[idx] = { ...initialData.cards[idx], ...val };
                    } else if (parts[2]) {
                      initialData.cards[idx][parts[2]] = val;
                    }
                  }
                }
              }
            }
          });
        }

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingMap);
        setSectionId(section.id || 42);
      }
    } catch (err) {
      toastRef.current?.error('Error loading quick links section: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadQuickLinksSection();
  }, [loadQuickLinksSection]);

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCardChange = (cardIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      cards: prev.cards.map((card, idx) =>
        idx === cardIndex ? { ...card, [field]: value } : card
      )
    }));
  };

  // Submit single field change (No duplicate submissions)
  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    if (formData[fieldName] === originalData[fieldName]) {
      toastRef.current?.warning('No changes detected for this field');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' '),
            old_value: originalData[fieldName],
            new_value: formData[fieldName],
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || result.ok) {
        toastRef.current?.success(`Changes for ${fieldName.replace(/_/g, ' ')} submitted for Principal review`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409 || result?.message?.includes('already has a pending')) {
          toastRef.current?.info('This field is already awaiting approval in the review queue');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toastRef.current?.error('Error submitting change: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Submit single card change (No duplicate submissions)
  const handleSubmitCard = async (cardIndex) => {
    if (!sectionId) {
      toastRef.current?.error('Section ID not loaded');
      return;
    }

    const fieldKey = `cards.${cardIndex}`;
    const currentCard = formData.cards[cardIndex];
    const originalCard = originalData.cards[cardIndex];

    if (JSON.stringify(currentCard) === JSON.stringify(originalCard)) {
      toastRef.current?.warning('No changes to submit for this card');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldKey,
            field_name: `Quick Link Card ${cardIndex + 1}`,
            old_value: originalCard,
            new_value: currentCard,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || result.ok) {
        toastRef.current?.success(`Card ${cardIndex + 1} submitted for Principal review`);
        
        setPendingFields(prev => ({
          ...prev,
          [fieldKey]: true
        }));

        setOriginalData(prev => ({
          ...prev,
          cards: prev.cards.map((card, idx) =>
            idx === cardIndex ? { ...currentCard } : card
          )
        }));
      } else {
        if (response.status === 409 || result?.message?.includes('already has a pending')) {
          toastRef.current?.info('This card is already awaiting approval in the review queue');
        } else {
          toastRef.current?.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toastRef.current?.error('Error submitting card: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const getCardIcon = (idx) => {
    if (idx === 0) return <UserCheck className="w-5 h-5 text-indigo-500" />;
    if (idx === 1) return <Calendar className="w-5 h-5 text-amber-500" />;
    return <Mail className="w-5 h-5 text-emerald-500" />;
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading quick links section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header & Actions */}
      <div className="unified-header">
        <div className="unified-header-left">
          <div className="unified-icon-badge">
            <Link2 className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="unified-header-text">
            <h1 className="unified-title">Quick Links Section</h1>
            <p className="unified-subtitle">Manage homepage navigation shortcuts and direct action cards</p>
          </div>
        </div>

        <div className="unified-header-actions">
          <button
            type="button"
            className="unified-secondary-btn"
            onClick={loadQuickLinksSection}
            title="Reload data from server"
          >
            <RefreshCw size={14} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      <div className="unified-card">
        {/* Section Header Controls */}
        <div>
          {/* Kicker */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Section Kicker</label>
                <p className="unified-field-desc">Small uppercase category tag above the main heading (e.g. "Quick Access")</p>
              </div>
              {pendingFields.kicker && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="100"
                className="unified-input"
                value={formData.kicker}
                onChange={(e) => handleFieldChange('kicker', e.target.value)}
                disabled={pendingFields.kicker}
                placeholder="e.g. Quick Access"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('kicker')}
                disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              >
                <Save size={14} /> <span>{submitting.kicker ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>

          {/* Section Title */}
          <div className="unified-field-card">
            <div className="unified-field-header">
              <div>
                <label className="unified-label">Section Heading Title</label>
                <p className="unified-field-desc">Main prominent heading for the quick links section</p>
              </div>
              {pendingFields.title && (
                <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
              )}
            </div>
            <div className="unified-input-action-row">
              <input
                type="text"
                maxLength="150"
                className="unified-input"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                disabled={pendingFields.title}
                placeholder="e.g. Everything you need in one place"
              />
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('title')}
                disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              >
                <Save size={14} /> <span>{submitting.title ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* 3 Quick Link Cards */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1e293b', marginBottom: '16px', letterSpacing: '-0.01em' }}>
            Quick Link Action Cards
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {formData.cards.map((card, idx) => {
              const fieldKey = `cards.${idx}`;
              const isPending = pendingFields[fieldKey];
              const isSubmittingThis = submitting[fieldKey];
              const isChanged = JSON.stringify(card) !== JSON.stringify(originalData.cards[idx]);

              return (
                <div 
                  key={card.id || idx} 
                  style={{
                    border: '1px solid #e2e8f0',
                    borderLeft: idx === 0 ? '4px solid #2563eb' : idx === 1 ? '4px solid #f59e0b' : '4px solid #059669',
                    borderRadius: '14px',
                    padding: '22px 24px',
                    background: isPending ? '#fffdf0' : (idx === 0 ? '#fafcff' : idx === 1 ? '#fffdfa' : '#f8fcfb'),
                    boxShadow: '0 2px 6px rgba(0,0,0,0.03)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {getCardIcon(idx)}
                      <strong style={{ fontSize: '15px', color: '#0f172a' }}>
                        Card {idx + 1}: {idx === 0 ? 'Apply Now' : idx === 1 ? 'School Calendar' : 'Contact Us'}
                      </strong>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {isPending && (
                        <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
                      )}
                      <button
                        type="button"
                        className="unified-submit-button"
                        onClick={() => handleSubmitCard(idx)}
                        disabled={isSubmittingThis || isPending || !isChanged}
                      >
                        <Save size={14} /> <span>{isSubmittingThis ? 'Saving...' : 'Save Card'}</span>
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
                    <div>
                      <label className="unified-label" style={{ fontSize: '12px' }}>Card Title</label>
                      <input
                        type="text"
                        className="unified-input"
                        value={card.title}
                        onChange={(e) => handleCardChange(idx, 'title', e.target.value)}
                        disabled={isPending}
                        placeholder="Card Title"
                      />
                    </div>
                    <div>
                      <label className="unified-label" style={{ fontSize: '12px' }}>Card Description</label>
                      <textarea
                        rows={2}
                        className="unified-textarea"
                        value={card.text}
                        onChange={(e) => handleCardChange(idx, 'text', e.target.value)}
                        disabled={isPending}
                        placeholder="Card Description"
                        style={{ minHeight: '65px' }}
                      />
                    </div>
                    <div>
                      <label className="unified-label" style={{ fontSize: '12px' }}>
                        Link Destination (When Card is Clicked)
                      </label>
                      <select
                        className="unified-input"
                        value={
                          customLinkMode[idx]
                            ? 'custom'
                            : (PRESET_LINKS.includes(card.link)
                                ? card.link
                                : (card.link ? 'custom' : (idx === 0 ? 'register' : idx === 2 ? 'contact-us#contact-form' : '#school-calendar')))
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'custom') {
                            setCustomLinkMode(prev => ({ ...prev, [idx]: true }));
                            if (PRESET_LINKS.includes(card.link)) {
                              handleCardChange(idx, 'link', '');
                            }
                          } else {
                            setCustomLinkMode(prev => ({ ...prev, [idx]: false }));
                            handleCardChange(idx, 'link', val);
                          }
                        }}
                        disabled={isPending}
                        style={{ background: '#ffffff', cursor: 'pointer' }}
                      >
                        <option value="register">Register / New Student Application (/register)</option>
                        <option value="contact-us#contact-form">Contact Us - Inquiry Form (/contact-us#contact-form)</option>
                        <option value="contact-us">Contact Us - Main Page (/contact-us)</option>
                        <option value="admissions">Admissions / Admissions Information (/admissions)</option>
                        <option value="education-program">Education Program / Academic Pathways (/education-program)</option>
                        <option value="student-life">Student Life / Student Activities (/student-life)</option>
                        <option value="facilities">Facilities / Campus Facilities (/facilities)</option>
                        <option value="news-and-updates">News and Updates / School News (/news-and-updates)</option>
                        <option value="parents">Parents / Parent Portal (/parents)</option>
                        <option value="careers">Careers / Job Openings (/careers)</option>
                        <option value="about-us">About Us / School Overview (/about-us)</option>
                        <option value="login">Portal Login (/login)</option>
                        <option value="#school-calendar">School Calendar / In-page Anchor (#school-calendar)</option>
                        <option value="custom">Custom Link / External URL (Type Manually)</option>
                      </select>

                      {(customLinkMode[idx] || (card.link && !PRESET_LINKS.includes(card.link))) && (
                        <div style={{ marginTop: '10px', background: '#f8fafc', padding: '14px 16px', borderRadius: '12px', border: '1.5px dashed #94a3b8' }}>
                          <label className="unified-label" style={{ fontSize: '12px', color: '#1e293b', fontWeight: '700', marginBottom: '6px', display: 'block' }}>
                            Enter Custom Link / External URL
                          </label>
                          <input
                            type="text"
                            className="unified-input"
                            value={card.link || ''}
                            onChange={(e) => handleCardChange(idx, 'link', e.target.value)}
                            disabled={isPending}
                            placeholder="Enter link (e.g. https://instagram.com/phs or /custom-page)"
                            autoFocus
                            style={{ background: '#ffffff', borderColor: '#3b82f6' }}
                          />
                          <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#64748b', lineHeight: 1.4 }}>
                            Use <strong>https://</strong> prefix for external websites, or <strong>/</strong> prefix for internal application routes.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
